import { useEffect } from "react";
import { SITE, absoluteUrl, toTagList } from "../utils/seo";

/**
 * Per page <head> manager.
 *
 * Tags are upserted (looked up by name/property and updated in place) instead of
 * appended, so the defaults in public/index.html - and the tags injected server
 * side by seo-render.php - are overwritten rather than duplicated.
 *
 * Multi valued / page specific tags (article:*, JSON-LD) are tracked with
 * data-seo-page and cleared before every write.
 */

const PAGE_ATTR = "data-seo-page";

const upsertMeta = (key, value, attribute = "name") => {
    if (!value) return;
    const head = document.head;
    let tag = head.querySelector(`meta[${attribute}="${key}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attribute, key);
        head.appendChild(tag);
    }
    tag.setAttribute("content", value);
};

const upsertCanonical = (href) => {
    if (!href) return;
    const head = document.head;
    let link = head.querySelector('link[rel="canonical"]');
    if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        head.appendChild(link);
    }
    link.setAttribute("href", href);
};

const appendPageMeta = (key, value, attribute = "property") => {
    if (!value) return;
    const tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    tag.setAttribute("content", value);
    tag.setAttribute(PAGE_ATTR, "");
    document.head.appendChild(tag);
};

const clearPageTags = () => {
    document.head
        .querySelectorAll(`[${PAGE_ATTR}]`)
        .forEach((node) => node.remove());
};

const Seo = ({
    title,
    description = SITE.defaultDescription,
    keywords = SITE.defaultKeywords,
    canonical,
    image,
    type = "website",
    robots = SITE.defaultRobots,
    // article / news only
    publishedTime,
    modifiedTime,
    section,
    tags,
    author = SITE.name,
    jsonLd,
}) => {
    const fullTitle = title ? `${title} | ${SITE.name}` : SITE.defaultTitle;
    const canonicalUrl = absoluteUrl(
        canonical || (typeof window !== "undefined" ? window.location.pathname : "/")
    );
    const imageUrl = absoluteUrl(image || SITE.ogImage);
    const articleTags = type === "article" ? toTagList(tags) : [];
    const structuredData = jsonLd ? JSON.stringify(jsonLd) : null;

    useEffect(() => {
        document.documentElement.setAttribute("lang", "hi");
        document.title = fullTitle;

        clearPageTags();

        upsertMeta("description", description);
        upsertMeta("keywords", keywords);
        upsertMeta("robots", robots);
        upsertMeta("author", author);
        upsertCanonical(canonicalUrl);

        upsertMeta("og:type", type, "property");
        upsertMeta("og:site_name", SITE.name, "property");
        upsertMeta("og:locale", SITE.locale, "property");
        upsertMeta("og:title", fullTitle, "property");
        upsertMeta("og:description", description, "property");
        upsertMeta("og:url", canonicalUrl, "property");
        upsertMeta("og:image", imageUrl, "property");
        upsertMeta("og:image:alt", title || SITE.name, "property");

        upsertMeta("twitter:card", "summary_large_image");
        upsertMeta("twitter:title", fullTitle);
        upsertMeta("twitter:description", description);
        upsertMeta("twitter:image", imageUrl);

        if (type === "article") {
            appendPageMeta("article:published_time", publishedTime);
            appendPageMeta("article:modified_time", modifiedTime || publishedTime);
            appendPageMeta("article:section", section || SITE.defaultSection);
            appendPageMeta("article:publisher", SITE.url);
            articleTags.forEach((tag) => appendPageMeta("article:tag", tag));
        }

        if (structuredData) {
            const script = document.createElement("script");
            script.type = "application/ld+json";
            script.setAttribute(PAGE_ATTR, "");
            script.textContent = structuredData;
            document.head.appendChild(script);
        }

        return clearPageTags;
    }, [
        title,
        fullTitle,
        description,
        keywords,
        robots,
        author,
        canonicalUrl,
        imageUrl,
        type,
        publishedTime,
        modifiedTime,
        section,
        structuredData,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        articleTags.join(","),
    ]);

    return null;
};

export default Seo;
